import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as RemoveIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import { opsApi } from '../../services/api';
import { ServiceCost, ServiceCostInput, ServiceCostPeriod, ServiceCostsSummary } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatDate } from '../../utils/format';
import { PERIOD_LABELS, money, shortMonth } from '../../utils/costs';
import ConfirmDialog from '../common/ConfirmDialog';
import AmountBars from './AmountBars';
import { CostPanel } from './CostPanel';

interface Props {
  summary: ServiceCostsSummary;
  /** Called after an entry was added, changed or removed, so the totals can be re-read. */
  onChanged: () => void;
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
}

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY: ServiceCostInput = { name: '', vendor: '', amount: '', period: 'monthly', started_on: today(), ended_on: null, notes: '' };

/** Costs no API reports (a domain, a subscription), kept by hand. */
const ServiceCosts: React.FC<Props> = ({ summary, onChanged, notify }) => {
  const [entries, setEntries] = useState<ServiceCost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ServiceCost | 'new' | null>(null);
  const [form, setForm] = useState<ServiceCostInput>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<ServiceCost | null>(null);

  const load = useCallback(async () => {
    try {
      setEntries(await opsApi.services());
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the entries. Please try again.'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = (entry: ServiceCost | 'new') => {
    setEditing(entry);
    setFormError(null);
    setForm(
      entry === 'new'
        ? { ...EMPTY, started_on: today() }
        : { name: entry.name, vendor: entry.vendor, amount: entry.amount, period: entry.period, started_on: entry.started_on, ended_on: entry.ended_on, notes: entry.notes }
    );
  };

  const save = async () => {
    if (!form.name.trim()) return setFormError('Give the cost a name.');
    if (!/^\d+(\.\d{1,2})?$/.test(form.amount.trim())) return setFormError('Enter the amount in dollars, for example 12 or 12.50.');
    if (!form.started_on) return setFormError('Say when the cost starts.');
    setSaving(true);
    setFormError(null);
    const data: ServiceCostInput = { ...form, name: form.name.trim(), vendor: form.vendor.trim(), amount: form.amount.trim(), notes: form.notes.trim(), ended_on: form.ended_on || null };
    try {
      if (editing === 'new') await opsApi.addService(data);
      else if (editing) await opsApi.updateService(editing.id, data);
      setEditing(null);
      notify(editing === 'new' ? `${data.name} added.` : `${data.name} updated.`);
      await load();
      onChanged();
    } catch (e) {
      setFormError(errorMessage(e, 'Could not save the entry.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setSaving(true);
    try {
      await opsApi.removeService(removing.id);
      notify(`${removing.name} removed.`);
      setRemoving(null);
      await load();
      onChanged();
    } catch (e) {
      notify(errorMessage(e, 'Could not remove the entry.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CostPanel
        title="Other services"
        description="Costs that no API reports to JobFlo: the domain, a subscription, a one-off purchase. Enter each once; a yearly cost is spread over twelve months."
      >
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
            {error}
          </Alert>
        )}
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => open('new')} sx={{ mb: 1.5 }}>
          Add a cost
        </Button>
        {entries && entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nothing entered yet. The domain registration is a good first entry.
          </Typography>
        )}
        {entries && entries.length > 0 && (
          <TableContainer>
            <Table size="small" aria-label="Costs entered by hand" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>How often</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>Until</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {entry.name}
                      {entry.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {entry.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{entry.vendor}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(Number(entry.amount))}</TableCell>
                    <TableCell>{PERIOD_LABELS[entry.period] ?? entry.period}</TableCell>
                    <TableCell>{formatDate(entry.started_on)}</TableCell>
                    <TableCell>{entry.ended_on ? formatDate(entry.ended_on) : entry.period === 'one_off' ? '' : 'Still paid'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" aria-label={`Edit ${entry.name}`} onClick={() => open(entry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton size="small" aria-label={`Remove ${entry.name}`} onClick={() => setRemoving(entry)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CostPanel>

      <CostPanel title="By month" description="What the entries above add to each month.">
        <AmountBars
          label="Entered costs by month"
          rows={summary.months.map((row) => ({ label: shortMonth(row.month), amount: row.amount }))}
          format={(amount) => money(amount)}
        />
      </CostPanel>

      <Dialog open={editing !== null} onClose={saving ? undefined : () => setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editing === 'new' ? 'Add a cost' : 'Edit cost'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus inputProps={{ maxLength: 120 }} />
            <TextField label="Vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} inputProps={{ maxLength: 120 }} />
            <TextField
              label="Amount in US dollars"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField select label="How often" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value as ServiceCostPeriod })}>
              {Object.entries(PERIOD_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={form.period === 'one_off' ? 'Paid on' : 'From'}
              type="date"
              value={form.started_on}
              onChange={(e) => setForm({ ...form, started_on: e.target.value })}
              required
              InputLabelProps={{ shrink: true }}
            />
            {form.period !== 'one_off' && (
              <TextField
                label="Until (empty while still paid)"
                type="date"
                value={form.ended_on ?? ''}
                onChange={(e) => setForm({ ...form, ended_on: e.target.value || null })}
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} inputProps={{ maxLength: 255 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        title="Remove this cost?"
        message={removing ? `${removing.name} will no longer count in any month's total.` : ''}
        confirmLabel="Remove"
        confirmColor="error"
        loading={saving}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
};

export default ServiceCosts;
